import { Trans, useLingui } from '@lingui/react/macro'
import { Button, Card, Group, Image, List, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconMail, IconPhone, IconSparkles } from '@tabler/icons-react'
import facePng from '../../../assets/images/face.png'
import { BookSurveyVisitModal } from './BookSurveyVisitModal'

interface ServicePromotionCardProps {
  title: string
  description: string
  benefits?: string[]
  ctaText?: string
  showAvailabilityNote?: boolean
}

export function ServicePromotionCard({
  title,
  description,
  benefits,
  ctaText,
  showAvailabilityNote = false,
}: ServicePromotionCardProps) {
  const { t } = useLingui()
  const [opened, { open, close }] = useDisclosure(false)
  const buttonText = ctaText || t`Book a free survey visit`

  return (
    <Card
      radius="lg"
      shadow="xs"
      style={{
        background: 'linear-gradient(135deg, #FFF4E6 0%, #FFE8CC 100%)',
        border: '1px solid var(--mantine-color-brandOrange-2)',
      }}
      p="lg"
    >
      <Stack gap="md">
        <Stack gap="sm">
          <Group gap="md" align="center" wrap="nowrap">
            <Image src={facePng} alt="Markku" w={56} h={56} fit="contain" />
            <Text fw={600} size="lg">
              {title}
            </Text>
          </Group>
          <Text>{description}</Text>
        </Stack>

        {benefits?.length && (
          <List
            spacing="xs"
            icon={
              <IconSparkles
                size={16}
                color="var(--mantine-color-brandOrange-6)"
              />
            }
          >
            {benefits.map((benefit, index) => (
              <List.Item key={index}>{benefit}</List.Item>
            ))}
          </List>
        )}

        {showAvailabilityNote && (
          <Text fs="italic">
            <Trans>
              Service available in the Helsinki metropolitan area and nearby
              regions
            </Trans>
          </Text>
        )}

        <Button
          fullWidth
          color="brandOrange"
          variant="filled"
          onClick={open}
          size="md"
        >
          {buttonText}
        </Button>

        <Group gap="xs" grow>
          <Button
            component="a"
            href="tel:0505555797"
            variant="outline"
            color="brandOrange"
            leftSection={<IconPhone size={18} />}
            size="sm"
          >
            050 5555 797
          </Button>
          <Button
            component="a"
            href="mailto:info@markku.fi"
            variant="outline"
            color="brandOrange"
            leftSection={<IconMail size={18} />}
            size="sm"
          >
            info@markku.fi
          </Button>
        </Group>
        <Text ta="center">
          <Trans>Our customer service is open on weekdays 8-16</Trans>
        </Text>
      </Stack>

      <BookSurveyVisitModal opened={opened} onClose={close} />
    </Card>
  )
}
